/**
 * Create-batch dialog (§12.1 Task 2) — choose the fulfilment mode (label-first /
 * one-by-one, default from App settings = PACK_FIRST) and which documents to
 * prepare. On confirm the caller creates the batch and (label-first) optionally
 * runs the carrier-label batch up front. A centred modal: scrim, Esc-to-cancel.
 */
import { useEffect, useState } from "react";
import type { FulfilmentMode } from "@cairn/shared";
import { Button, Icon } from "../../components/ui/index.js";
import { modeHint } from "../../lib/batchStatus.js";

export interface CreateBatchOptions {
  mode: FulfilmentMode;
  note: string;
  pickingList: boolean;
  packingSlips: boolean;
  /** Label-first only: create every order's carrier label now (mock). */
  runLabels: boolean;
}

export function CreateBatchDialog({
  count,
  busy,
  onConfirm,
  onCancel,
}: {
  count: number;
  busy?: boolean;
  onConfirm: (opts: CreateBatchOptions) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<FulfilmentMode>("PACK_FIRST");
  const [note, setNote] = useState("");
  const [pickingList, setPickingList] = useState(true);
  const [packingSlips, setPackingSlips] = useState(true);
  const [runLabels, setRunLabels] = useState(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  return (
    <div className="modal-layer">
      <div className="modal-scrim" onClick={onCancel} aria-hidden="true" />
      <div className="modal" role="dialog" aria-modal="true" aria-label="Create batch">
        <header className="modal__head">
          <h2>Create batch</h2>
          <Button iconOnly icon="x" variant="ghost" size="sm" aria-label="Cancel" onClick={onCancel} />
        </header>
        <p className="modal__sub">
          <strong className="tnum">{count}</strong> order{count === 1 ? "" : "s"} selected.
        </p>

        <div className="modal__body">
          <fieldset className="batch-mode">
            <legend>Fulfilment mode</legend>
            {(["PACK_FIRST", "LABEL_FIRST"] as const).map((m) => (
              <label key={m} className={"batch-mode__opt" + (mode === m ? " batch-mode__opt--on" : "")}>
                <input type="radio" name="batch-mode" checked={mode === m} onChange={() => setMode(m)} />
                <span className="batch-mode__main">
                  <span className="batch-mode__name">
                    {m === "PACK_FIRST" ? "Pack-first — one by one" : "Label-first — batch labels"}
                    {m === "PACK_FIRST" && <span className="batch-mode__default">App default</span>}
                  </span>
                  <span className="batch-mode__hint">{modeHint(m)}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <fieldset className="batch-docs">
            <legend>Documents</legend>
            <label className="check">
              <input type="checkbox" checked={pickingList} onChange={(e) => setPickingList(e.target.checked)} />
              <Icon name="clipboard" size={14} /> Picking list
            </label>
            <label className="check">
              <input type="checkbox" checked={packingSlips} onChange={(e) => setPackingSlips(e.target.checked)} />
              <Icon name="printer" size={14} /> Packing slips
            </label>
            {mode === "LABEL_FIRST" && (
              <label className="check">
                <input type="checkbox" checked={runLabels} onChange={(e) => setRunLabels(e.target.checked)} />
                <Icon name="layers" size={14} /> Create carrier labels now <span className="muted">(mock)</span>
              </label>
            )}
          </fieldset>

          <label className="field">
            <span className="field__label">Note (optional)</span>
            <input
              className="control input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Morning GLS run"
            />
          </label>
        </div>

        <footer className="modal__foot">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="layers"
            disabled={busy || count === 0}
            onClick={() => onConfirm({ mode, note: note.trim(), pickingList, packingSlips, runLabels })}
          >
            {busy ? "Creating…" : "Create batch"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
